import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Paper, Box, Button, Typography, useTheme, Alert } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Summarize as SummarizeIcon,
} from '@mui/icons-material';

const CriteriaNavigationBar = ({
  currentIndex,
  totalCriteria,
  currentLabel,
  onPrevious,
  onNext,
  isNextDisabled,
  isPreviousDisabled,
  advancingToSummary,
  showSummaryGuard,
  pendingSectionLabels = [],
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (totalCriteria <= 0) return null;

  const nextLabel = advancingToSummary ? t('submission.summary') : t('common.next');
  const previousLabel = currentIndex <= 0 ? t('common.back') : t('common.previous');

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'sticky',
        bottom: 16,
        bgcolor: theme.palette.background.paper,
        borderRadius: 2,
        p: 2.5,
        border: theme => `1px solid ${theme.palette.borderColor?.main || theme.palette.divider}`,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.3)',
        zIndex: 2,
      }}>
      <Box sx={{ flexGrow: 1, minWidth: 220 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {t('submission.stepProgress', { current: currentIndex + 1, total: totalCriteria })}
        </Typography>
        {currentLabel ? (
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {currentLabel}
          </Typography>
        ) : null}
        {showSummaryGuard ? (
          <Alert severity="warning" sx={{ mt: 1.5 }} icon={false}>
            <Typography variant="body2">
              {pendingSectionLabels.length
                ? t('submission.summaryGuardTitle', {
                    sections: pendingSectionLabels.join(', '),
                  })
                : t('submission.completeRequirementsBeforeSummary')}
            </Typography>
          </Alert>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onPrevious}
          disabled={isPreviousDisabled}
          sx={{
            fontWeight: 600,
            borderColor: theme => theme.palette.borderColor?.main || theme.palette.divider,
            color: 'text.primary',
            minWidth: 140,
          }}>
          {previousLabel}
        </Button>
        <Button
          variant={theme.palette.mode === 'dark' ? 'outlined' : 'contained'}
          endIcon={advancingToSummary ? <SummarizeIcon /> : <ArrowForwardIcon />}
          onClick={onNext}
          disabled={isNextDisabled}
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            minWidth: 160,
            color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#000',
            bgcolor: theme.palette.mode === 'dark' ? 'transparent' : theme.palette.primary.main,
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 210, 0, 0.5)' : 'transparent',
            '&:disabled': {
              color: 'text.disabled',
              borderColor: 'divider',
              bgcolor: 'action.disabledBackground',
            },
          }}>
          {nextLabel}
        </Button>
      </Box>
    </Paper>
  );
};

CriteriaNavigationBar.propTypes = {
  currentIndex: PropTypes.number.isRequired,
  totalCriteria: PropTypes.number.isRequired,
  currentLabel: PropTypes.string,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  isNextDisabled: PropTypes.bool,
  isPreviousDisabled: PropTypes.bool,
  advancingToSummary: PropTypes.bool,
  showSummaryGuard: PropTypes.bool,
  pendingSectionLabels: PropTypes.arrayOf(PropTypes.string),
};

export default CriteriaNavigationBar;
